import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View, Text, TouchableOpacity, TextInput, ScrollView,
  Modal, Platform, Animated, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Constants from 'expo-constants';
import appJson from '../app.json';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { useFeedback } from '../context/FeedbackContext';
import { db } from '../firebaseConfig';
import { collection, query as fsQuery, orderBy, limit as fsLimit, getDocs } from 'firebase/firestore';

const APP_VERSION = appJson?.expo?.version || Constants?.expoConfig?.version || '1.0.0';

const getBase = () => (
  (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
  || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
  || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
);

const FEEDBACK_TYPES = [
  { key: 'query_accuracy', label: 'Query Accuracy', icon: 'search', color: '#8b5cf6' },
  { key: 'missing_results', label: 'Missing Results', icon: 'alert-circle', color: '#f59e0b' },
  { key: 'performance', label: 'Performance', icon: 'speedometer', color: '#3b82f6' },
  { key: 'feature_request', label: 'Feature Request', icon: 'bulb', color: '#10b981' },
  { key: 'bug', label: 'Bug Report', icon: 'bug', color: '#ef4444' },
  { key: 'general', label: 'General', icon: 'chatbubble-ellipses', color: '#6b7280' },
];

export default function FeedbackPill() {
  const { user, userProfile } = useAuth();
  const { isAdmin, navigateTo } = useNavigation();
  const { queryHistory, recentResultsMap } = useBackgroundQuery();
  const { open, setOpen, prefilledQuery, setPrefilledQuery, closeFeedback } = useFeedback();

  const [selectedType, setSelectedType] = useState(null);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  // State-based close lock: disables pill pointer-events while the modal is closing
  const [pillDisabled, setPillDisabled] = useState(false);
  // Fallback queries loaded from Firestore when context history is empty and a query-specific category is selected
  const [fallbackQueries, setFallbackQueries] = useState([]);
  const [fallbackQueriesLoading, setFallbackQueriesLoading] = useState(false);

  // Timer ref — cleared on close so we never fire setOpen(false) against stale state
  const closeTimerRef = useRef(null);
  // Ref-based lock — secondary guard for handleOpen (checked synchronously)
  const justClosedRef = useRef(false);
  const justClosedTimerRef = useRef(null);

  // Pulse animation
  const pulseAnim = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1.12, duration: 2000, useNativeDriver: true }),
        Animated.timing(pulseAnim, { toValue: 1, duration: 2000, useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [pulseAnim]);

  // When the modal opens with a prefilledQuery, auto-select it
  useEffect(() => {
    if (open && prefilledQuery) {
      setSelectedQuery(prefilledQuery);
      setSelectedType('query_accuracy');
    }
  }, [open, prefilledQuery]);

  // When a query-specific category is selected and the context has no recent queries,
  // lazily load the last 5 from Firestore so the user has something to select
  useEffect(() => {
    const isQueryCategory = selectedType === 'query_accuracy' || selectedType === 'missing_results';
    if (!isQueryCategory || !user?.uid) return;
    // Inline the recentQueries derivation — can't reference the derived variable here (declared after early returns)
    const contextHasQueries = (queryHistory || []).some(q => q.status === 'completed' && q.prompt);
    if (contextHasQueries || fallbackQueries.length > 0) return; // already showing
    let cancelled = false;
    (async () => {
      setFallbackQueriesLoading(true);
      try {
        const snap = await getDocs(
          fsQuery(
            collection(db, 'users', user.uid, 'query_history'),
            orderBy('createdAt', 'desc'),
            fsLimit(10)
          )
        );
        if (cancelled) return;
        const docs = snap.docs
          .map(d => ({ queryId: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate?.() || null }))
          .filter(d => d.prompt)
          .slice(0, 5);
        setFallbackQueries(docs);
      } catch (e) {
        console.warn('[FeedbackPill] fallback query load failed', e.message);
      } finally {
        if (!cancelled) setFallbackQueriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedType, user?.uid, queryHistory, fallbackQueries.length]);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (justClosedTimerRef.current) clearTimeout(justClosedTimerRef.current);
    };
  }, []);

  // ALL hooks declared before any early returns
  const handleSubmit = useCallback(async () => {
    if (!text.trim() && !selectedQuery) {
      setError('Please enter some feedback or select a recent search.');
      return;
    }
    const finalText = text.trim() || (selectedQuery ? `Feedback on query: "${selectedQuery.prompt}"` : '');
    if (finalText.length < 3) {
      setError('Feedback is too short.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const base = getBase();
      // Attach results snapshot if available from in-memory map
      const resultsSnapshot = selectedQuery?.queryId
        ? (recentResultsMap?.get(selectedQuery.queryId) || null)
        : null;
      const payload = {
        uid: user.uid,
        userEmail: user.email || null,
        appVersion: APP_VERSION,
        feedbackType: selectedType || 'general',
        text: finalText,
        relatedQuery: selectedQuery ? {
          queryId: selectedQuery.queryId,
          prompt: selectedQuery.prompt,
          resultsCount: selectedQuery.resultsCount ?? null,
          createdAt: selectedQuery.createdAt || null,
        } : null,
        resultsSnapshot: resultsSnapshot ? resultsSnapshot.slice(0, 20) : null,
        userTier: userProfile?.tier || null,
      };
      const resp = await fetch(`${base}/submitFeedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await resp.json();
      if (!resp.ok || !json.success) throw new Error(json.error || 'Submission failed');
      setSubmitted(true);
      setText('');
      setSelectedType(null);
      setSelectedQuery(null);
      // Auto-close after 3s, but user can also tap Done / X / backdrop
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
      }, 3000);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [text, selectedType, selectedQuery, user, userProfile, recentResultsMap, setOpen]);

  // Early returns after all hooks
  if (Platform.OS !== 'web') return null;
  if (!user) return null;

  const adminPillVisible = isAdmin && userProfile?.preferences?.showEnvPill !== false;
  // AdminEnvPill is ~34px tall + 8px gap
  const bottomOffset = adminPillVisible ? 58 : 16;

  const recentQueries = (queryHistory || [])
    .filter(q => q.status === 'completed' && q.prompt)
    .slice(0, 5);

  const handleOpen = () => {
    if (justClosedRef.current) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setPillDisabled(false);
    setPrefilledQuery(null);
    setSubmitted(false);
    setError(null);
    setSelectedType(null);
    setSelectedQuery(null);
    setText('');
    setOpen(true);
  };

  const handleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    // Dual-layer click-through protection:
    // 1. Ref guard (synchronous): handleOpen returns early while this is true
    // 2. State guard (reactive): pill View gets pointerEvents="none" so the click never reaches handleOpen
    justClosedRef.current = true;
    setPillDisabled(true);
    if (justClosedTimerRef.current) clearTimeout(justClosedTimerRef.current);
    justClosedTimerRef.current = setTimeout(() => {
      justClosedRef.current = false;
      setPillDisabled(false);
    }, 500);
    setOpen(false);
    setSubmitted(false);
    setError(null);
    setPrefilledQuery(null);
    setFallbackQueries([]);
  };

  const handleSelectQuery = (q) => {
    setSelectedQuery(prev => prev?.queryId === q.queryId ? null : q);
    if (!selectedType) setSelectedType('query_accuracy');
  };

  return (
    <>
      {/* Floating pill — pointer-events disabled briefly after modal closes to prevent click-through */}
      <View style={[st.pillContainer, { bottom: bottomOffset, pointerEvents: pillDisabled ? 'none' : 'auto' }]}>
        <TouchableOpacity style={st.pill} onPress={handleOpen} activeOpacity={0.8}>
          <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
            <Ionicons name="chatbubble-ellipses" size={14} color="#a78bfa" />
          </Animated.View>
          <Text style={st.pillText}>Feedback</Text>
        </TouchableOpacity>
      </View>

      {/* Modal — no fade animation on web avoids the lingering-overlay click-through problem */}
      <Modal visible={open} transparent animationType={Platform.OS === 'web' ? 'none' : 'fade'} onRequestClose={handleClose}>
        <TouchableOpacity style={st.backdrop} activeOpacity={1} onPress={handleClose}>
          <TouchableOpacity activeOpacity={1} style={st.sheet} onPress={() => {}}>

            {/* Header */}
            <View style={st.sheetHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Ionicons name="chatbubble-ellipses" size={16} color="#a78bfa" />
                <Text style={st.sheetTitle}>Share Feedback</Text>
              </View>
              <TouchableOpacity onPress={handleClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <Ionicons name="close" size={18} color="rgba(255,255,255,0.4)" />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false} style={{ maxHeight: 540 }}>
              {submitted ? (
                <View style={st.successBox}>
                  <Ionicons name="checkmark-circle" size={44} color="#10b981" />
                  <Text style={st.successTitle}>Thanks for the feedback!</Text>
                  <Text style={st.successSub}>It helps us build a better product.</Text>
                  <TouchableOpacity style={st.doneBtn} onPress={handleClose}>
                    <Text style={st.doneBtnText}>Done</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <>
                  {/* Recent queries — up to 5 (always shown when context has history) */}
                  {recentQueries.length > 0 && (
                    <View style={st.section}>
                      <Text style={st.sectionLabel}>Recent searches — tap to give specific feedback</Text>
                      {recentQueries.map((q) => {
                        const active = selectedQuery?.queryId === q.queryId;
                        return (
                          <TouchableOpacity
                            key={q.queryId}
                            style={[st.queryChip, active && st.queryChipActive]}
                            onPress={() => handleSelectQuery(q)}
                            activeOpacity={0.7}
                          >
                            <Ionicons name="search" size={12} color={active ? '#a78bfa' : 'rgba(255,255,255,0.4)'} />
                            <Text style={[st.queryChipText, active && { color: '#a78bfa' }]} numberOfLines={1}>
                              {q.prompt}
                            </Text>
                            {q.resultsCount != null && (
                              <Text style={st.queryChipCount}>{q.resultsCount} results</Text>
                            )}
                            {active && <Ionicons name="checkmark-circle" size={13} color="#a78bfa" />}
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  )}

                  {/* Fallback query list — shown when a query-specific category is picked but context history is empty */}
                  {recentQueries.length === 0 && (selectedType === 'query_accuracy' || selectedType === 'missing_results') && (
                    <View style={st.section}>
                      <Text style={st.sectionLabel}>Your recent searches — tap to attach one</Text>
                      {fallbackQueriesLoading ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 10 }}>
                          <ActivityIndicator size="small" color="#a78bfa" />
                          <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>Loading your searches…</Text>
                        </View>
                      ) : fallbackQueries.length > 0 ? (
                        fallbackQueries.map((q) => {
                          const active = selectedQuery?.queryId === q.queryId;
                          return (
                            <TouchableOpacity
                              key={q.queryId}
                              style={[st.queryChip, active && st.queryChipActive]}
                              onPress={() => handleSelectQuery(q)}
                              activeOpacity={0.7}
                            >
                              <Ionicons name="search" size={12} color={active ? '#a78bfa' : 'rgba(255,255,255,0.4)'} />
                              <Text style={[st.queryChipText, active && { color: '#a78bfa' }]} numberOfLines={1}>
                                {q.prompt}
                              </Text>
                              {q.resultsCount != null && (
                                <Text style={st.queryChipCount}>{q.resultsCount} results</Text>
                              )}
                              {active && <Ionicons name="checkmark-circle" size={13} color="#a78bfa" />}
                            </TouchableOpacity>
                          );
                        })
                      ) : (
                        <Text style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, paddingVertical: 6 }}>
                          No past searches found.
                        </Text>
                      )}
                    </View>
                  )}

                  {/* Category */}
                  <View style={st.section}>
                    <Text style={st.sectionLabel}>Category{!selectedType ? ' (optional)' : ''}</Text>
                    <View style={st.typeGrid}>
                      {FEEDBACK_TYPES.map(ft => {
                        const active = selectedType === ft.key;
                        return (
                          <TouchableOpacity
                            key={ft.key}
                            style={[st.typeChip, active && { backgroundColor: ft.color + '25', borderColor: ft.color + '80' }]}
                            onPress={() => setSelectedType(active ? null : ft.key)}
                            activeOpacity={0.7}
                          >
                            <Ionicons name={ft.icon} size={12} color={active ? ft.color : 'rgba(255,255,255,0.4)'} />
                            <Text style={[st.typeChipText, active && { color: ft.color }]}>{ft.label}</Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>

                  {/* Text input */}
                  <View style={st.section}>
                    <Text style={st.sectionLabel}>
                      {selectedQuery
                        ? `How was the accuracy for "${selectedQuery.prompt.slice(0, 40)}${selectedQuery.prompt.length > 40 ? '…' : ''}"?`
                        : 'Tell us more'}
                    </Text>
                    <TextInput
                      style={st.textInput}
                      placeholder={
                        selectedQuery
                          ? 'Were results accurate? Anything missing?'
                          : "What's on your mind? Bugs, missing features, praise — anything helps."
                      }
                      placeholderTextColor="rgba(255,255,255,0.25)"
                      value={text}
                      onChangeText={setText}
                      multiline
                      numberOfLines={4}
                      maxLength={2000}
                      textAlignVertical="top"
                    />
                    <Text style={st.charCount}>{text.length}/2000</Text>
                  </View>

                  {/* Settings hint — tappable link */}
                  <TouchableOpacity
                    style={st.hintBox}
                    activeOpacity={0.7}
                    onPress={() => {
                      handleClose();
                      navigateTo('Profile', { payload: { tab: 'queryHistory' }, force: true });
                    }}
                  >
                    <Ionicons name="information-circle-outline" size={13} color="rgba(255,255,255,0.3)" />
                    <Text style={st.hintText}>
                      For your full search history tap{' '}
                      <Text style={st.hintLink}>Settings → Query History</Text>
                      {' '}to browse all past searches and leave feedback on any of them.
                    </Text>
                    <Ionicons name="chevron-forward" size={12} color="rgba(167,139,250,0.5)" />
                  </TouchableOpacity>

                  {error && (
                    <View style={st.errorBox}>
                      <Ionicons name="alert-circle" size={13} color="#ef4444" />
                      <Text style={st.errorText}>{error}</Text>
                    </View>
                  )}

                  <TouchableOpacity
                    style={[st.submitBtn, submitting && { opacity: 0.6 }]}
                    onPress={handleSubmit}
                    disabled={submitting}
                    activeOpacity={0.8}
                  >
                    {submitting
                      ? <ActivityIndicator size="small" color="#fff" />
                      : <>
                          <Ionicons name="send" size={14} color="#fff" />
                          <Text style={st.submitText}>Send Feedback</Text>
                        </>
                    }
                  </TouchableOpacity>
                </>
              )}
            </ScrollView>

          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

const st = {
  pillContainer: {
    position: 'fixed',
    left: 16,
    zIndex: 99998,
    alignItems: 'flex-start',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(20, 20, 24, 0.95)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(167,139,250,0.25)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  pillText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 12,
    fontWeight: '600',
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    paddingLeft: 16,
    paddingBottom: 72,
  },
  sheet: {
    backgroundColor: 'rgba(18, 18, 22, 0.98)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    width: 370,
    maxWidth: '95vw',
    padding: 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.5,
    shadowRadius: 32,
    elevation: 20,
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sheetTitle: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },
  section: {
    marginBottom: 16,
  },
  sectionLabel: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  queryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    paddingHorizontal: 11,
    paddingVertical: 8,
    marginBottom: 6,
  },
  queryChipActive: {
    backgroundColor: 'rgba(167,139,250,0.1)',
    borderColor: 'rgba(167,139,250,0.35)',
  },
  queryChipText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    flex: 1,
  },
  queryChipCount: {
    color: 'rgba(255,255,255,0.25)',
    fontSize: 10,
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  typeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  typeChipText: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 11,
    fontWeight: '500',
  },
  textInput: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    color: '#fff',
    fontSize: 13,
    padding: 12,
    minHeight: 90,
    outlineStyle: 'none',
  },
  charCount: {
    color: 'rgba(255,255,255,0.2)',
    fontSize: 10,
    textAlign: 'right',
    marginTop: 4,
  },
  hintBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: 8,
    padding: 10,
    marginBottom: 12,
  },
  hintText: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
    lineHeight: 16,
    flex: 1,
  },
  hintLink: {
    color: 'rgba(167,139,250,0.7)',
    fontWeight: '600',
  },
  submitBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    backgroundColor: '#7c3aed',
    borderRadius: 10,
    paddingVertical: 12,
    marginTop: 4,
    marginBottom: 4,
  },
  submitText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(239,68,68,0.1)',
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
  },
  errorText: {
    color: '#ef4444',
    fontSize: 12,
    flex: 1,
  },
  successBox: {
    alignItems: 'center',
    paddingVertical: 32,
    gap: 12,
  },
  successTitle: {
    color: '#fff',
    fontSize: 17,
    fontWeight: '700',
  },
  successSub: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: 13,
  },
  doneBtn: {
    marginTop: 8,
    backgroundColor: 'rgba(16,185,129,0.15)',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(16,185,129,0.3)',
    paddingHorizontal: 32,
    paddingVertical: 10,
  },
  doneBtnText: {
    color: '#10b981',
    fontSize: 14,
    fontWeight: '700',
  },
};
